import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, EventEmitter, OnChanges, SimpleChanges, Input, Output, SimpleChange, HostListener } from '@angular/core';
import { formatNumber } from '@angular/common';
import { Observable, concat, from, of, EMPTY, Subscription, Subject } from 'rxjs';
import { map, switchMap, concatMap, debounceTime, distinctUntilChanged, filter, finalize, catchError } from 'rxjs/operators';

import { MenuItem, MessageService, FilterService } from 'primeng/api';
import { Table } from 'primeng/table';
import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';
import { round1, round2 } from 'src/app/shared/round';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { Document, DocItems, DocumentStatusFlag } from 'src/app/shared/document';
import { Product, ProductInfo } from 'src/app/shared/product';
import { Currency } from 'src/app/shared/currency';
import { ProductApiService } from 'src/app/services/product-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';

/**
 * Inline-editing table for payment documents (DOCTYPEs 8, 9, 11).
 *
 * **Pipeline architecture:**
 * - `docItemList$` — debounced `switchMap(getDocItemsByDoc)` that reloads rows when the
 *   document ID changes; triggers `calculateDocItemsFields` enrichment per row.
 * - `calcDocSum$` — debounced (100 ms) Subject that aggregates `DOCSUM*` totals and emits
 *   a `DocumentStatusFlag` via `getDocumentStatus`.
 *
 * **Simplified enrichment:** `calculateDocItemsFields` performs only basic VAT math — no
 * product-info or stock API calls.  `calculateRowSummary` handles DOCTYPEs 8/9/11 with
 * straightforward sum calculation.
 *
 * **Single-select:** `sDI` is a single `DocItems` object (not an array), and
 * `onDeleteConfirmation` deletes the single selected item via `deleteDocItemById`.
 *
 * Column visibility is persisted in `localStorage` under key `edit-document-table-columns-9`.
 */
@Component({
  selector: 'app-edit-document-content-payment',
  templateUrl: './edit-document-content-payment.component.html',
  styleUrls: ['./edit-document-content-payment.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EditDocumentContentPaymentComponent implements OnInit, OnDestroy, OnChanges {

  private globalSettinsData: GlobalSettingsData = null;
  private docItemQuery: Subscription = null;
  private diSumQuery: Subscription = null;
  private diSaveQuery: Subscription = null;
  private diGroupQuery: Subscription = null;

  /** Debounced Subject that re-aggregates document totals and emits `getDocumentStatus`. */
  private calcDocSum$ = new Subject<Document>();
  private calcDocSumSubs: Subscription = null;

  /** Whether to render the table header toolbar. */
  @Input() showHeader: boolean = true;
  /** Whether to show add/edit/delete action controls. */
  @Input() allowEditControls: boolean = true;

  /** When true, rows with zero `DOCITEMQTY` are included in printed forms. */
  @Input() countEmpty: boolean = false;
  /** Whether to show payment-sum footer row. */
  @Input() showPaysum: boolean = true;

  loading: boolean = false;

  typeDeleteConfirmation: boolean = false;
  confirmText: string = 'DELETE 12';
  deleteAllRows: boolean = false;

  selectedDocType: number;

  sDI: DocItems = null;

  allDocItemsChanged: boolean = false;

  docItems: DocItems[] = [];

  docItemsCopy: DocItems[] = [];
  docItemsClone: DocItems[] = [];

  u4etkaMultiplier: number = 1;

  yesno = [
    { label: 'Да', value: 'Да' },
    { label: 'Нет', value: 'Нет' },
    { label: '', value: '' }
  ];

  @ViewChild('docItemTable') docItemTable: Table;

  //@HostListener('keydown.space', ['$event'])
  //onSpaceKeyDown(event: KeyboardEvent) {
  //  event.stopPropagation();
  //  event.preventDefault();
  //  if (event.target) {
  //    event.target['value'] = event.target['value'] + ' ';
  //    (event.target as HTMLElement).focus();
  //  }
  //}

  editMode: boolean = false;
  confirmDeleteOn: boolean = false;

  private docItemList$ = new Subject<number>();

  /** The document whose line items are being edited. */
  @Input() inputDocument: Document = null;

  /** Reserved trigger input — currently unused, kept for API compatibility. */
  @Input() knockKnock: boolean = false;

  private storeNameShowRowOptions: string = 'edit-document-table-columns-9';

  showDocRowsOptions = [
    { name: 'Без НДС', value: 2 },
    { name: 'Примечание', value: 16 },
  ];

  showFilterOptions = [
    { name: 'Платеж', icon: 'pi pi-filter', value: 1, disabled: false },
  ];

  showFilterValue: number[] = [1];

  public get showFilter(): boolean { return this.showFilterValue.includes(1); }

  allowOld: boolean = false;

  /** Overrides column-visibility bitmask from the caller; skips localStorage restore when set. */
  @Input() setShowRowOptions: number[];

  showRowOptions: number[] = [];

  public get noItems(): boolean { return ((this.docItems || []).length < 1); }

  public get seeWoVAT(): boolean { return this.showRowOptions.includes(2); }

  public get seeDiNote(): boolean { return ([1, 12].includes(this.inputDocument.DOCTYPE) && this.showRowOptions.includes(16)); }

  public get selection(): boolean { return this.sDI ? true : false; }

  public get changes(): boolean { return this.editMode || this.allDocItemsChanged; }

  public allowSave(di: DocItems): boolean { return di && ((di.PRODID ?? -1) != -1); }

  cf3: any = {};

  /** Emits `true` when entering edit mode, `false` when edit is saved or cancelled. */
  @Output() editing: EventEmitter<boolean> = new EventEmitter();

  // Initial value indicated serious issue in document items.
  // would clear during the calculation of document profit and emitted to
  // parent component

  documentItemsStatus: number = 101;

  /** Emits a `DocumentStatusFlag` integer after each `calcDocSum$` aggregation cycle. */
  @Output() getDocumentStatus: EventEmitter<number> = new EventEmitter();

  /** Two-way binding companion for `countEmpty`; emits when the user toggles zero-qty inclusion. */
  @Output() countEmptyChange: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private prodApi: ProductApiService,
    private messageService: MessageService,
    private docApi: DocumentApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private filterService: FilterService,
  ) {
    this.docItemQuery =
      this.docItemList$.pipe(
        debounceTime(300),
        switchMap((docId: number) => {
          this.loading = true;
          console.log('*** EditDocumentContentPaymentComponent docItemList$ subscribing....: ' + docId)

          return this.docApi.getDocItemsByDoc(docId);

        })
      )
        .subscribe(data => {

          console.log('*** EditDocumentContentPaymentComponent docItemList$ subscription: ')
          //environment.production || console.table(data);

          this.calculateDocItemsFields(data);     // <= Raw docItems[] from database
          this.calculateDocumentSummary();        // <= Processed docItems[] with all calculated fields
          // <= Here we have also document summary calculated
          this.loading = false;
        });

    // Below thread calculate document summary on every change of docItems array
    // in general it is fast and could be called every time when something changed

    // Also this thread check the overall status of document items and emit it to parent component

    this.calcDocSumSubs = this.calcDocSum$.pipe(

      debounceTime(100),
      filter(doc => (doc !== null)),

      concatMap(doc => {

        environment.production || console.log('*** EditDocumentContentComponent calcDocSum$ start: ');

        doc.DOCITEMS = (this.docItems || []).filter(e => this.countEmpty || (e.DOCITEMQTY != 0)).length;
        doc.DOCTOTALITEMS = this.docItems.filter(e => this.countEmpty || (e.DOCITEMQTY != 0)).reduce((r, di) => r + di.DOCITEMQTY, 0);

        doc.DOCVSUM = this.docItems.reduce((r, di) => r + di.DOCITEMQTY * di.DOCITEMVPRICE, 0);
        doc.DOCSUMWOVAT = this.docItems.reduce((r, di) => r + di.DOCITEMSUMWOVAT, 0);
        doc.DOCVAT = this.docItems.reduce((r, di) => r + (di.DOCITEMSUM - di.DOCITEMSUMWOVAT), 0);
        doc.DOCSUM = this.docItems.reduce((r, di) => r + di.DOCITEMSUM, 0);

        this.documentItemsStatus = 0;

        return from(this.docItems).pipe(
          map(di => {

            /* doctypes.includes(1,12)
              рахунок фактура
              комерційна пропозиція
        
                  + (di.bip ? 'Цена ниже входной! ' : '')
                  + (di.bmd ? 'Скидка больше максимальной! ' : '')
                  + (di.cnm ? 'Валюта не соответствует установленной в документе! ' : '')
                  + (di.rnm ? 'Курс не соответствует установленному в документе! ' : '')
                  + (di.cpn ? 'Валютная цена не соответствует расчетной! ' : '')
                  + (di.mtc ? 'Не указан УКТЗЕД товара! ' : '')
                  + (di.nta ? 'Не подтвержден УКТЗЕД товара! ' : '')
                  + (di.lpf ? 'Возможный убыток от операции! ' : '')
                  + (di.npc ? 'Не установлена категория товара ' : '')
 
                  di.hlt = (di.bip || di.bmd || di.cnm || di.rnm || di.mtc || di.npc || di.nta || di.lpf || di.cpn);
 
                  di.hlt set to true means that some document item has some issue
 
                  Even single serious issue (bip, lpf) set documentItemsStatus to SeriousError

            if (!di.hlt) {

              if (di.bip || di.cnm || di.rnm || di.mtc || di.lpf) {
                this.documentItemsStatus = DocumentStatusFlag.SeriousError.value;
              }

              if (this.documentItemsStatus < DocumentStatusFlag.SeriousError.value) {

                if (di.cnm || di.rnm || di.mtc || di.lpf) {
                  this.documentItemsStatus = DocumentStatusFlag.MinorIssue.value;
                }
              }

              // Here we need to review all the isse and set final status of document
              // this.documentItemsStatus = 1;
            }
            environment.production || console.log('*** EditDocumentContentComponent in calcDocSum$: ' + JSON.stringify({ DI: di.DOCITEMID, HL: di.hlt }));
          */
          }),

          finalize(() => {

            let status = this.docItems.some(di => di.hlt) ? 1 : 0;
            environment.production || console.log('*** EditDocumentContentComponent in calcDocSum$ finalize: ' + JSON.stringify({ ST: status, DI: this.documentItemsStatus }));
            this.getDocumentStatus.emit(status);
          })
        );
      }))
      .subscribe();
  }

  reloadDocContent(event) {
    console.log('*** EditDocumentContentPaymentComponent reloadDocContent: ' + JSON.stringify(event))

    if (this.inputDocument) {
      this.docItemList$.next(this.inputDocument.DOCID);
    }
  }

  saveDiCopy() {
    this.docItemsCopy = [];
    this.docItems.forEach(e => this.docItemsCopy.push({ ...e }));
  }

  restoreDiCopy() {
    this.docItems = [];
    this.docItemsCopy.forEach(e => this.docItems.push({ ...e }));
  }

  /** Calculates `DOCITEMSUM*` fields for DOCTYPEs 8, 9, 11 and triggers a document summary update. */
  calculateRowSummary(di: DocItems) {
    //console.log('*** EditDocumentContentPaymentComponent calculateRowSummary: ' + di.DOCITEMID);

    switch (di.DOCTYPE) {

      case 8:
      case 9:
      case 11:

        di.DOCITEMSUMWOVAT = di.DOCITEMQTY * di.DOCITEMPRICEWOVAT;
        di.DOCITEMSUM = di.DOCITEMQTY * di.DOCITEMPRICE;
        di.DOCITEMVSUM = di.DOCITEMQTY * di.DOCITEMVPRICE;
        di.DOCITEMSUMU4 = di.DOCITEMQTY * di.U4ETKA;
        break;
    }

    this.calculateDocumentSummary();
  }

  calculateDocumentSummary() {
    //console.log('*** EditDocumentContentPaymentComponent calculateDocumentSummary: ')

    this.calcDocSum$.next(this.inputDocument);
  }

  /** VAT multiplier (e.g. 1.2) for a row: the item's own `PRODVAT` (0 is valid), else the global default rate. */
  private vatFactor(di: DocItems): number {
    return (100.0 + (di.PRODVAT ?? this.globalSettinsData.vatValue)) / 100.0;
  }

  /** Populates `docItems` from raw API data, computing VAT and currency price fields synchronously (no extra API calls). */
  calculateDocItemsFields(data: DocItems[]) {
    //console.log('*** EditDocumentContentPaymentComponent calculateDocItemsFields')
    this.docItems = [...data];

    this.diSumQuery =
        from(this.docItems)
          .pipe(
            concatMap(di => {
              di.cloneIndex = 0;
              di.DOCITEMPRICEWOVAT = (di.DOCITEMPRICE / this.vatFactor(di));
              di.DOCITEMVPRICE = round2(di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL);

              this.calculateRowSummary(di);
              return of(di);
            }),

            finalize(() => {
              this.inputDocument.items = this.docItems;
              console.log('*** EditDocumentContentPaymentComponent calculateDocItemsFields => finalize')

              this.saveDiCopy();
            }))
        .subscribe();
  }

  private handleHttpError<T>(operation: string, result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed:`, error);
      const detail = error?.status === 0 ? `Немає з'єднання з сервером`
        : error?.status >= 500 ? 'Помилка сервера'
        : error?.message || 'Невідома помилка';
      this.messageService.add({ severity: 'error', summary: 'Помилка завантаження', detail, life: 5000 });
      return result !== undefined ? of(result) : (EMPTY as Observable<T>);
    };
  }

  /** Persists column visibility to localStorage and unsubscribes all active subscriptions. */
  ngOnDestroy(): void {
    console.log('*** EditDocumentContentPaymentComponent ngOnDestroy: ')

    if (!this.setShowRowOptions) {
      localStorage.setItem(this.storeNameShowRowOptions, JSON.stringify(this.showRowOptions));
    }
    localStorage.setItem(this.storeNameShowRowOptions + '-columns-filter-state', JSON.stringify(this.showFilterValue));

    if (this.docItemQuery) { this.docItemQuery.unsubscribe(); }
    if (this.calcDocSumSubs) { this.calcDocSumSubs.unsubscribe(); }
    if (this.diSaveQuery) { this.diSaveQuery.unsubscribe(); }
    if (this.diSumQuery) { this.diSumQuery.unsubscribe(); }
    if (this.diGroupQuery) { this.diGroupQuery.unsubscribe(); }
  }

  /**
   * Reacts to `inputDocument`, `knockKnock`, and `setShowRowOptions` changes.
   * - `inputDocument`: pushes the document ID onto `docItemList$` to reload rows.
   * - `setShowRowOptions`: overrides the column visibility bitmask immediately.
   */
  ngOnChanges(changes: SimpleChanges): void {
    //    console.log('*** EditDocumentContentPaymentComponent ngOnDestroy: ')
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** EditDocumentContentPaymentComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //      console.log('*** EditDocumentContentPaymentComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'setShowRowOptions':
          this.showRowOptions = [...change.currentValue];
          break;

        case 'inputDocument':
          if (change.currentValue) {
            this.docItemList$.next(change.currentValue.DOCID);
          }
          break;
      }
    }
  }

  /** Registers custom PrimeNG filter strategies, restores column state from localStorage, and wires up `docItemList$` and `calcDocSum$` pipelines. */
  ngOnInit(): void {
    console.log('*** EditDocumentContentPaymentComponent ngOnInit: ')

    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value > filter))
    )
    );

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value >= filter))
    )
    );

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    if (!this.setShowRowOptions) {
      let _storedShowRowOptions: string = localStorage.getItem(this.storeNameShowRowOptions);
      if (_storedShowRowOptions) { this.showRowOptions = JSON.parse(_storedShowRowOptions); }
    }

    let _storedFilterOptions: string = localStorage.getItem(this.storeNameShowRowOptions + '-columns-filter-state');
    if (_storedFilterOptions) { this.showFilterValue = JSON.parse(_storedFilterOptions); }

    this.reloadDocContent(null);
  }

  onRowsOptionChange(event) {
    //  console.log('*** EditDocumentContentPaymentComponent in doChangeConditions: ' + JSON.stringify(event));
    //  console.log('*** EditDocumentContentPaymentComponent in doChangeConditions (showRowOptions): ' + JSON.stringify(this.showRowOptions));
  }

  onProductSelection(event: Product, di: DocItems) {
    console.log('*** EditDocumentContentPaymentComponent onProductSelection: ' + JSON.stringify(event));
    di.PRODID = event.PRODID;
    di.PRODCODE = event.PRODCODE;
    di.PRODVAT = event.PRODVAT;
  }

  onAddRow(event) {
    console.log('*** EditDocumentContentPaymentComponent in onAddRow: ' + JSON.stringify(event));

    let maxRowIndex = 0;

    this.docItems.forEach(pp => { maxRowIndex = Math.max(maxRowIndex, pp.PAYITEMCLIID) });

    let newDi: DocItems = {

      DOCITEMID: -2,
      DOCID: this.inputDocument.DOCID,
      DOCTYPE: this.inputDocument.DOCTYPE,
      PRODID: null,
      DOCITEMPRODNAME: '',
      DOCITEMPRICE: 0,
      DOCITEMQTY: 1,
      DOCITEMVPRICE: 0,
      DOCITEMCURRID: this.inputDocument.DOCCURRID,
      DOCITEMCRATE: this.inputDocument.DOCRATEVAL,
      PAYITEMCLIID: maxRowIndex + 10,
      PAYITEMDEPID: null,
      DOCITEMTEXT1: '',
      DOCITEMTEXT2: 'Нет',
      CURCODE: '',
      U4ETKA: null,
      PRODVAT: this.globalSettinsData.vatValue,
      cloneIndex: 0
    };

    this.docItems = [...this.docItems, newDi];

    let ppe: DocItems = this.docItems.find(e => e.DOCITEMID == -2);
    this.onDiRowEditInit(ppe);
    this.docItemTable.initRowEdit(ppe);
  }

  onDiRowEditInit(esg: DocItems) {
    console.log('*** EditDocumentContentPaymentComponent in onPpRowEditInit: entry');
    //environment.production || console.table(esg);

    let newCloneIndex: number = 0;

    /*
     * Quite tricky algoritm:
     * Because we can edit simultaneously more than one row in the table.
     * Therefore for every edited row we have to create cloned copy in shadow array [securityGroupsClone]
     * We can't predict the order of starting and ending the edit process
     * 
     * So - Search for max cloneIndex in current table and assign +1 to newly created row copy
     *
     * When row either cancelled or saved - clone index resetted to 0, clone copy deleted from shadow array
     *
     */
    this.docItems.forEach(pp => { newCloneIndex = Math.max(newCloneIndex, pp.cloneIndex) });

    console.log('*** EditDocumentContentPaymentComponent in onPpRowEditInit: newCloneIndex is: ' + newCloneIndex);

    esg.cloneIndex = ++newCloneIndex;
    this.docItemsClone.push({ ...esg });

    esg.DOCITEMCURRID = this.inputDocument.DOCCURRID;
    esg.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;

    this.editMode = true;
    this.editing.emit(this.editMode);
  }

  onDiRowEditCancel(esg: DocItems, index: number) {
    console.log('*** EditDocumentContentPaymentComponent in onPpRowEditCancel: ' + index);
    //environment.production || console.table(esg);

    let ppid = this.docItemsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);

    if (esg.DOCITEMID == -2) {
      // Cancel the newly created record
      // simply delete

      this.docItems.splice(index, 1);

    } else {
      // Cancel editing of existing record
      // have to reload it from database

      this.docItems[index] = { ...this.docItemsClone[ppid] };
      this.docItems[index].cloneIndex = 0;
    }

    this.docItemsClone.splice(ppid, 1);

    this.editMode = false;
    this.editing.emit(this.editMode);

    //  console.log('*** EditDocumentContentPaymentComponent in onPpRowEditSave: prodParts');
    //  environment.production || console.table(this.prodParts);
    //  console.log('*** EditDocumentContentPaymentComponent in onPpRowEditSave: prodPartsClone ');
    //  environment.production || console.table(this.prodPartsClone);
  }

  onDiRowEditSave(esg: DocItems) {
    console.log('*** EditDocumentContentPaymentComponent in onDiRowEditSave: ');

    let ppid = this.docItemsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);
    esg.DOCITEMVPRICE = esg.DOCITEMPRICE / esg.DOCITEMCRATE;

    this.diSaveQuery =
      concat(((esg.DOCITEMID == -2) ? this.docApi.postDocItemById(esg) : this.docApi.putDocItemById(esg))
        .pipe(map(count => {
          console.log('*** EditDocumentContentPaymentComponent in onSubscribe to Save: ' + count);

          this.docItemsClone.splice(ppid, 1);
          esg.cloneIndex = 0;
          this.reloadDocContent(null);
          this.editMode = false;
          this.editing.emit(this.editMode);
        })))
        .subscribe();
  }

  onDOCITEMQTYChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentPaymentComponent in onDOCITEMQTYChange: ' + JSON.stringify(event));
    //console.log('*** EditDocumentContentPaymentComponent in onDOCITEMQTYChange: ' + JSON.stringify(di));

    this.calculateRowSummary(di);
  }

  onDOCITEMVPRICEChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentPaymentComponent in onDOCITEMVPRICEChange: ' + JSON.stringify(event));
    //console.log('*** EditDocumentContentPaymentComponent in onDOCITEMQTYChange: ' + JSON.stringify(di));

    di.DOCITEMPRICE = round2(di.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL);
    di.DOCITEMPRICEWOVAT = round2(di.DOCITEMPRICE / this.vatFactor(di));

    this.calculateRowSummary(di);
  }

  onDOCITEMPRICEWOVATChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentPaymentComponent in onDOCITEMPRICEWOVATChange: ' + JSON.stringify(event));
    console.log('*** EditDocumentContentPaymentComponent in onDOCITEMPRICEWOVATChange: ' + JSON.stringify(di));

    di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * this.vatFactor(di));
    di.DOCITEMVPRICE = round2(di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL);

    this.calculateRowSummary(di);
  }

  onDOCITEMPRICEChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentPaymentComponent in onDOCITEMPRICEChange: ' + JSON.stringify(event));
    console.log('*** EditDocumentContentPaymentComponent in onDOCITEMPRICEChange: ' + JSON.stringify(di));

    di.DOCITEMPRICEWOVAT = round1(di.DOCITEMPRICE / this.vatFactor(di));
    di.DOCITEMVPRICE = round2(di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL);

    this.calculateRowSummary(di);
  }

  onPAYITEMCLIIDChange(event, di: DocItems) {
    console.log('*** EditDocumentContentPaymentComponent in onPAYITEMCLIIDChange: ' + JSON.stringify(event));
    console.log('*** EditDocumentContentPaymentComponent in onPAYITEMCLIIDChange: ' + JSON.stringify(di));
  }

  checkDiState(event, di: DocItems) {
    console.log('*** EditDocumentContentPaymentComponent in checkDiState: ' + JSON.stringify(event));
    console.log('*** EditDocumentContentPaymentComponent in checkDiState: ' + JSON.stringify(di));
    if (di.hlt) {
      this.messageService.add({
        severity: 'warn',
        summary: di.PRODCODE + ' информация: ',
        life: 10000,
        detail: ''
          + (di.bip ? 'Цена ниже входной! ' : '')
          + (di.bmd ? 'Скидка больше максимальной! ' : '')
          + (di.cnm ? 'Валюта не соответствует установленной в документе! ' : '')
          + (di.rnm ? 'Курс не соответствует установленному в документе! ' : '')
          + (di.mtc ? 'Не указан УКТЗЕД товара! ' : '')
      });
    } else {
      this.messageService.add({ severity: 'success', summary: di.PRODCODE + ' информация: OK' });
    }
  }

  onRowSelect(event) {
    console.log('*** EditDocumentContentPaymentComponent in onRowSelect: ' + JSON.stringify(event));
  }

  updCurrPrice(event) {
    console.log('*** EditDocumentContentPaymentComponent in updCurrPrice: ' + JSON.stringify(event));
    this.diGroupQuery
      = from(this.docItems)
        .pipe(switchMap(di => of(di)))
        .subscribe(e => {
          e.DOCITEMCURRID = this.inputDocument.DOCCURRID;
          e.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;
          e.DOCITEMVPRICE = e.DOCITEMPRICE / e.DOCITEMCRATE;
          this.calculateRowSummary(e);
        });

    this.allDocItemsChanged = true;
    this.editing.emit(this.allDocItemsChanged);
  }

  updMainPrice(event) {
    console.log('*** EditDocumentContentPaymentComponent in updMainPrice: ' + JSON.stringify(event));
    this.diGroupQuery
      = from(this.docItems)
        .pipe(switchMap(di => of(di)))
        .subscribe(e => {
          e.DOCITEMCURRID = this.inputDocument.DOCCURRID;
          e.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;
          e.DOCITEMPRICE = round2(e.DOCITEMVPRICE * e.DOCITEMCRATE);
          this.calculateRowSummary(e);
        });

    this.allDocItemsChanged = true;
    this.editing.emit(this.allDocItemsChanged);
  }

  onSaveRows(event) {
    console.log('*** EditDocumentContentPaymentComponent in onSaveRows: ' + JSON.stringify(event));

    let count = this.docItems.length;

    this.docItems.forEach(e => {
      this.diSaveQuery = this.docApi.putDocItemById(e).pipe(map(() => { this.reloadDocContent(null); })).subscribe(() => {
        console.log('*** EditDocumentContentPaymentComponent in onSaveRows: count: ' + count);
        if (--count == 0) {
          this.calculateDocItemsFields(this.docItems);
          this.allDocItemsChanged = false;
          this.editing.emit(this.allDocItemsChanged);
        }
      });
    });
  }

  onUndoRows(event) {
    console.log('*** EditDocumentContentPaymentComponent in onUndoRows: ' + JSON.stringify(event));

    this.restoreDiCopy();
    this.calculateDocItemsFields(this.docItems);
    this.allDocItemsChanged = false;
    this.editing.emit(this.allDocItemsChanged);
  }

  onRemoveAllRow(event) {
    console.log('*** EditDocumentContentPaymentComponent in onRemoveRow: ' + JSON.stringify(event));

    this.deleteAllRows = true;
    this.confirmText = 'DELETE ' + this.docItems.length;
    this.typeDeleteConfirmation = true;
    this.confirmDeleteOn = true;
  }

  onRemoveRow(event) {
    console.log('*** EditDocumentContentPaymentComponent in onRemoveRow: ' + JSON.stringify(event));
    this.deleteAllRows = false;
    this.typeDeleteConfirmation = false;
    this.confirmDeleteOn = true;
  }

  /** Deletes the currently selected single row (or all rows when `deleteAllRows` is true) via `deleteDocItemById`. */
  onDeleteConfirmation(event) {
    console.log('*** EditDocumentContentPaymentComponent onDeleteConfirmation: ' + JSON.stringify(event));
    this.confirmDeleteOn = false;

    if (event) {
      console.log('*** EditDocumentContentPaymentComponent onDeleteConfirmation DO DELETE!!! NOW!!!');

      if (this.deleteAllRows) {
        console.log('*** EditDocumentContentPaymentComponent onDeleteConfirmation DO DELETE ALL !!! NOW!!! ********** ALL ********');
        this.docApi.deleteAllDocItemByDocId(this.inputDocument.DOCID)
          .subscribe(() => {
            console.log('*** EditDocumentContentPaymentComponent onDeleteAllItemsConfirmation: DONE!');
            this.sDI = null;
            this.reloadDocContent(null);
          });
      } else {
        if (this.sDI) {
          this.docApi.deleteDocItemById(this.sDI.DOCITEMID)
            .subscribe(() => {
              console.log('*** EditDocumentContentPaymentComponent onDeleteConfirmation: DONE!');
              this.sDI = null;
              this.reloadDocContent(null);
            });
        }
      }
    }
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });

      saveAs(
        data,
        fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
      );
    });
  }

  /*
  DOCID  DOCTYPEID  DOCDATE  DOCEXPDATE  DOCVALDATE  DOCCLIENTSID  DOCPERSONID
  DOCMANCLIID  DOCMANPERID  DOCNUMBER  DOCNOTE1  DOCNOTE2  PRIMDOCID  DOCCLIDEPID
  DOCMANDEPID  DOCCURRID  DOCRATEVAL  DOCACTIVE  DOCFLAG  CLIENTSNAME  DOCLCOCLIID
  LOCALCOMP  PERSONNAME  DOCNAME  DOCSUM  PAYDSUM  SHIPSUM  AUTHOR  CURSTR  LOCALCOMPID
  PAIDJUSTNOW  PAIDNOWDATE  DOCPCOCLIID  PARTNERNAME  PARTNERNAMESTR
  fs	ps	fp	pp	np	go	td	fd
  */

  onExportData() {
    console.log('*** EditDocumentContentPaymentComponent onExportData: ')

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.docItems);

      let exportData: any =
        this.docItems
          .filter(e => this.countEmpty || (e.DOCITEMQTY != 0))
          .map(e => ({

          'Код товара': e.PRODCODE,
          'Наименование товара': e.DOCITEMPRODNAME,
          'Количество': e.DOCITEMQTY,
          'Цена с НДС': e.DOCITEMPRICE,
          'Сумма с НДС': e.DOCITEMSUM,
          'Цена в валюте': e.DOCITEMVPRICE,
          'Сумма в валюте': e.DOCITEMVSUM,
          'Цена без НДС': e.DOCITEMPRICEWOVAT,
          'Сумма без НДС': e.DOCITEMSUMWOVAT,
          'Текст 1': e.DOCITEMTEXT1,
          'Текст 2': e.DOCITEMTEXT2,
          'Валюта': e.CURCODE,
          'Курс': e.DOCITEMCRATE,
        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { document: worksheet }, SheetNames: ['document'] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "document");
    });
  }

  onStateSave(state) {
    console.log('*** EditDocumentContentPaymentComponent onStateSave: ' + JSON.stringify(state));
  }

  onStateRestore(state) {
    console.log('*** EditDocumentContentPaymentComponent onStateRestore: ' + JSON.stringify(state));
    if (state.filters) {
      for (let f in state.filters) {
        let fltItem = state.filters[f];
        this.cf3[f] = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;
      }
    }
  }

  cleanSortMode(state) {
    console.log('*** EditDocumentContentPaymentComponent cleanSortMode: ' + JSON.stringify(state));

    this.docItemTable.reset();
    this.reloadDocContent(state);
  }

  updateRowIndexes(state) {
    console.log('*** EditDocumentContentPaymentComponent updateRowIndexes: ' + JSON.stringify(state));

    let rowIndex = 10;

    this.diSaveQuery =
      concat(
        from(this.docItems)
          .pipe(concatMap(di => {
            di.PAYITEMCLIID = rowIndex;
            rowIndex += 10;

            return this.docApi.putDocItemById(di);
          })),

        of(false).pipe(map(data => this.loading = data))
      )
        .subscribe();
  }

  countEmptyModeChange(event) {
    console.log('*** EditDocumentContentComponent countEmptyModeChange: ' + JSON.stringify(event));

    this.countEmpty = event.checked;

    if (this.inputDocument) {
      this.docItemList$.next(this.inputDocument.DOCID);
      this.countEmptyChange.emit(this.countEmpty);
    }
  }
}
